import type { User } from '~/services/auth.server'
import type { MouseEventHandler } from 'react'
import { Fragment, useEffect, useState } from 'react'

import { LanguageSelector } from '~/components/LanguageSelector'

import ttpLogo from 'public/images/logo-light.svg'

import ttpLogoDark from 'public/images/logo-dark.svg'
import DashboardIcon from '~/components/icons/Dashboard'
import { ArrowDownIcon } from '~/components/icons/ArrowDownIcon'

import { useTranslation } from 'react-i18next'
import { Menu as HeadlessMenu, Transition } from '@headlessui/react'
import { Link, useFetcher, useLocation } from '@remix-run/react'
import type { Account, Language } from '~/types/api-client'
import { AccountState, NS } from '~/enums'
import { setAccountStatusTextColor } from '~/helpers/string'
import LoaderIcon from './icons/Loader'
import { handleDashboardRedirection } from '~/helpers/dashboard'
import { ROUTES } from '~/components/quick-checkout/QuickCheckout.config'
import { classNames as cn } from '~/helpers/string'

//import { isPromoValidFn } from '~/helpers/challenges'

import { Menu, MenuTrigger, MenuContent, MenuItem, Avatar } from '~/components/shared'

interface TopBarProps {
  user?: User | null
  locale: string
  languages: Language[]
  isNavOpen?: boolean
  navToggle?: MouseEventHandler
  variant?: 'dark' | 'light'
}

export default function TopBar(props: TopBarProps) {
  const { user, locale, isNavOpen, navToggle, languages, variant = 'light' } = props
  const location = useLocation() // Get the current route path

  const hideLanguageSelector = location.pathname.startsWith('/affiliate')
  // const showPromoBanners = isPromoValidFn(appUrl)

  const darkStyles = 'bg-brand-violet-950 text-brand-white'
  const lightStyles = 'border border-b border-brand-gray-200 bg-brand-white text-brand-violet-950'
  const isLight = variant === 'light'

  return (
    <div
      className={cn(
        'topbar top-0 z-50 flex w-full items-center justify-between px-4 py-2.5 sm:px-7.5',
        isLight ? lightStyles : darkStyles,
        location.pathname.startsWith(ROUTES.main) ? '' : 'fixed',
      )}
    >
      <div className="flex items-center gap-4 sm:gap-8">
        {user && (
          <button
            type="button"
            className={`navbar-toggler ${isNavOpen ? 'z-50' : 'collapsed'}`}
            aria-label="Toggle navigation"
            onClick={navToggle}
          >
            <span className={isLight ? 'bg-brand-violet-950' : 'bg-brand-white'}></span>
            <span className={isLight ? 'bg-brand-violet-950' : 'bg-brand-white'}></span>
            <span className={isLight ? 'bg-brand-violet-950' : 'bg-brand-white'}></span>
            <span className={isLight ? 'bg-brand-violet-950' : 'bg-brand-white'}></span>
          </button>
        )}
        <Link to={location.pathname.startsWith(ROUTES.main) ? ROUTES.main : '/'} className="flex items-center gap-2">
          <img src={isLight ? ttpLogoDark : ttpLogo} className="h-8 w-auto sm:h-10" alt="TTP" />
        </Link>
      </div>

      {/* PROMO BANNERS */}
      {/* {showPromoBanners && (
        <>
          <div className="mx-auto hidden 2xl:inline">
            <img src={`/images/banners/promo-big-${locale}.jpg`} alt="xmas-promo" />
          </div>
          <div className="mx-auto hidden lg:inline 2xl:hidden">
            <img src={`/images/banners/promo-small-${locale}.png`} alt="xmas-promo" />
          </div>
        </>
      )} */}

      <div className="flex gap-4 md:gap-6">
        {/* {user && <DashboardAccounts />} */}
        {!hideLanguageSelector && <LanguageSelector locale={locale} languages={languages} />}
        {user && <UserMenu {...user} />}
      </div>
    </div>
  )
}

function UserMenu(props: User) {
  const { clientId, email, clientType, clientContact, displayClientId } = props

  let { t } = useTranslation()

  let crypto = require('crypto')
  let hashedClientId = crypto.createHash('sha256').update(clientId).digest('hex')
  let hashedEmail = crypto.createHash('sha256').update(email).digest('hex')

  const fullName = clientContact?.firstName + ' ' + clientContact?.lastName
  const firstName = clientContact?.firstName

  return (
    <Menu>
      <MenuTrigger>
        <button
          // IMPORTANT: id and data attributes for GTM
          id="profile-menu-btn"
          data-client-id={hashedClientId}
          data-client-email={hashedEmail}
          data-client-type={clientType?.stateName}
          className="flex h-full items-center gap-2.5"
        >
          <Avatar name={fullName} src="" size="md" />
          <span className="hidden text-sm font-normal sm:inline">{firstName}</span>
        </button>
      </MenuTrigger>
      <MenuContent>
        <div className="mb-2.5 flex flex-row items-center gap-3">
          <Avatar name={fullName} src="" size="lg" />
          <div className="flex flex-1 flex-col gap-1 overflow-hidden">
            <span className="line-clamp-1 text-sm font-medium text-base-content">{fullName}</span>
            {
              <span className="line-clamp-1 text-sm font-light text-base-content">{`${t('client_id', { ns: NS.PERSONAL_INFO })}: ${displayClientId}`}</span>
            }
          </div>
        </div>
        <MenuItem to="/profile">{t('my_profile', { ns: NS.GLOBALS })}</MenuItem>

        <form action="/logout" method="post" onSubmit={() => localStorage.removeItem('checkout')}>
          <button
            type="submit"
            className={'block w-full rounded-md px-3 py-2 text-left text-sm text-base-content hover:bg-primary-150'}
          >
            {t('logout', { ns: NS.GLOBALS })}
          </button>
        </form>
      </MenuContent>
    </Menu>
  )
}

function DashboardAccounts() {
  let { t } = useTranslation()

  const tradingAccounts = useFetcher<Account[]>()

  const [searchValue, setSearchValue] = useState<string>('')
  // const [isActiveOnly, setIsActiveOnly] = useState<boolean>(false)

  const [showAllAccounts, setShowAllAccounts] = useState<boolean>(false)

  const [isLoading, setIsLoading] = useState<boolean>(false)

  const accounts = t('dashboard', { ns: NS.GLOBALS })
  // const showOnlyActive = t('show_active_accounts_only', { ns: NS.UI })
  const noActiveAccounts = t('no_active_accounts_found', { ns: NS.UI })

  const showAllAccountsLabel = t('show_all_accounts', { ns: NS.UI })

  // When showAllAccounts is true, all accounts will be included, and when it's false, only active accounts will be included
  const filteredAccounts = tradingAccounts.data?.length
    ? tradingAccounts.data?.filter((item) => {
        const isMatchingSearch = !searchValue || item.tradingPlatformAccountId?.includes(searchValue)
        const shouldIncludeAccount = showAllAccounts || item.accountState.name === AccountState.ACTIVE

        // Return true only if both conditions are met
        return isMatchingSearch && shouldIncludeAccount
      })
    : []

  useEffect(() => {
    if (tradingAccounts.state === 'idle' && tradingAccounts.data) {
      setIsLoading(false)
    }
  }, [tradingAccounts])

  const loadTradingAccounts = () => {
    setIsLoading(true)
    tradingAccounts.load(`/trading-accounts`)
  }

  return (
    <HeadlessMenu as="div" className="relative inline-block justify-center text-left">
      {({ open }) => (
        <>
          <div className="flex items-center justify-center rounded-lg border border-primary-200 px-2 py-2">
            <HeadlessMenu.Button
              className="flex flex-row items-center gap-2"
              onClick={() => !open && loadTradingAccounts()}
            >
              <DashboardIcon className="text-primary-300" width="23px" height="26px" />
              <span className="hidden text-sm md:inline">{accounts}</span>
              <ArrowDownIcon className={`h-5 w-5 text-primary-300 ${open ? 'rotate-180' : ''}`} />
            </HeadlessMenu.Button>
          </div>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <HeadlessMenu.Items className="absolute mt-1 w-72 origin-top-right -translate-x-1/2 rounded-lg border border-base-300 bg-white p-3 shadow-lg focus:outline-none md:right-0 md:-translate-x-0 rtl:translate-x-1/2 md:rtl:translate-x-0">
              <div className="relative mb-3 w-full">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <img src={'/images/search.svg'} alt="" />
                </div>
                <input
                  name="search"
                  className="w-full pl-10 text-sm"
                  type="search"
                  placeholder="Search"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  disabled={!tradingAccounts.data?.length}
                />
              </div>
              {tradingAccounts.data?.length && (
                <div className="mb-3 flex gap-2">
                  <input
                    id="show-all"
                    type="checkbox"
                    name="show-all"
                    checked={showAllAccounts}
                    onChange={() => setShowAllAccounts(!showAllAccounts)}
                  />
                  <label className="text-xs text-base-content" htmlFor="show-all">
                    {showAllAccountsLabel}
                  </label>
                </div>
              )}
              {/* : (
                !isLoading && <span className="text-xs text-base-content">{noAccounts}</span>
              )} */}

              {isLoading && (
                <div className="flex justify-center pb-4 pt-2">
                  <LoaderIcon />
                </div>
              )}

              {!showAllAccounts && !filteredAccounts?.length && (
                <span className="text-xs text-base-content">{noActiveAccounts}</span>
              )}

              <div className="max-h-52 overflow-y-auto text-base-content">
                {filteredAccounts?.map((account: Account, index: number) => (
                  <HeadlessMenu.Item key={`account-${index}`}>
                    {({ active }) => (
                      <button
                        className={`${
                          active ? 'bg-primary-100' : ''
                        } group flex w-full items-center rounded-md px-1 py-2 text-xs`}
                        onClick={() => handleDashboardRedirection(account)}
                      >
                        <div className="flex w-full flex-row items-center justify-between">
                          <div className="flex flex-col gap-1 text-start">
                            <div className="font-semibold">{account.tradingPlatformAccountId}</div>
                            <div>{account.offerDescription}</div>
                          </div>
                          <div className="flex flex-row items-center gap-1">
                            <span className={setAccountStatusTextColor(account.accountState.name)}>
                              {account.accountState.displayName}
                            </span>
                            <img
                              src={`/images/account-status/${account.accountState.name.toLowerCase()}.svg`}
                              alt={account.accountState.displayName}
                              className="h-3 w-3"
                            />
                          </div>
                        </div>
                      </button>
                    )}
                  </HeadlessMenu.Item>
                ))}
              </div>
            </HeadlessMenu.Items>
          </Transition>
        </>
      )}
    </HeadlessMenu>
  )
}
